import React, { forwardRef, useRef } from 'react';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

export interface FileUploadProps {
  label?: string;
  accept?: string;
  multiple?: boolean;
  maxSize?: number; // in bytes
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  value?: File | File[] | null;
  onChange: (files: File | File[] | null) => void;
}

const CustomFileUpload = forwardRef<HTMLInputElement, FileUploadProps>(
  (
    {
      label = 'Upload File',
      accept,
      multiple = false,
      maxSize,
      disabled = false,
      error = false,
      helperText,
      value,
      onChange,
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement | null>(null);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length === 0) {
        onChange(null);
        return;
      }

      const selectedFiles = multiple ? Array.from(files) : files[0];

      // Size validation
      if (maxSize) {
        const invalid = Array.from(files).some((file) => file.size > maxSize);
        if (invalid) {
          onChange(null);
          return;
        }
      }

      onChange(selectedFiles);
    };

    const renderFileName = () => {
      if (!value) return null;

      if (Array.isArray(value)) {
        return value.map((file) => file.name).join(', ');
      }

      return value.name;
    };

    return (
      <Stack spacing={0.5}>
        <Button
          variant="outlined"
          component="label"
          disabled={disabled}
          color={error ? 'error' : 'primary'}
        >
          {label}
          <input
            ref={(node) => {
              inputRef.current = node;
              if (typeof ref === 'function') ref(node);
            }}
            type="file"
            hidden
            accept={accept}
            multiple={multiple}
            onChange={handleChange}
          />
        </Button>

        {value && (
          <Typography variant="caption" sx={{ mt: 0.5 }}>
            Selected: {renderFileName()}
          </Typography>
        )}

        {helperText && (
          <Typography
            variant="caption"
            color={error ? 'error' : 'text.secondary'}
          >
            {helperText}
          </Typography>
        )}
      </Stack>
    );
  }
);

CustomFileUpload.displayName = 'CustomFileUpload';

export default CustomFileUpload;
